import {
  AUCTION_REQUEST_ACCEPTED,
  AUCTION_REQUEST_SENT,
  MAX_AGE,
  SCHEMA_VERSION,
} from "core/consts";
import {
  BarthelAssessment,
  ClinicalPatientProfile,
  EarlyRehabAssessment,
  EncryptedContent,
  FilterValueStartDateRange,
  SchemaVersion,
  SingerAssessment,
} from "core/types";
import {
  addDays,
  differenceInDays,
  differenceInHours,
  differenceInYears,
  endOfDay,
  format,
  formatDistance,
  fromUnixTime,
  getUnixTime,
  isBefore,
  isFuture,
  isMatch,
  isPast,
  isSameDay,
  isToday,
  isValid,
  isWithinInterval,
  isYesterday,
  parse,
  startOfDay,
  startOfYear,
  subDays,
  subMonths,
  subYears,
} from "date-fns";
import {
  format as formatInBerlin,
  utcToZonedTime,
  zonedTimeToUtc,
} from "date-fns-tz";
import de from "date-fns/locale/de";
import enGB from "date-fns/locale/en-GB";
import {
  convertEncryptedFieldIn,
  convertEncryptedFieldOut,
} from "react-forms-state";
import { Locale as LocaleString } from "translations";
import Translations from "translations/types";
import { getErrorMessage } from "../errors";

type FormatUnixDateOptions = {
  customFormat?: string;
  withTime?: boolean;
  withoutYear?: boolean;
};

export function getAgeFromDate(date: string) {
  let format = "dd.MM.yyyy";
  if (isMatch(date, "dd/MM/yyyy")) {
    format = "dd/MM/yyyy";
  }
  const birthDate = parse(date, format, new Date());
  return differenceInYears(new Date(), birthDate);
}

export function getAgeDisplayFromBirthDate(
  birthDate: ClinicalPatientProfile["birth_date"],
  fallback: string,
): string {
  if (!birthDate?.decrypted) return fallback;
  try {
    return String(getAgeFromDate(birthDate.decrypted));
  } catch {
    return fallback;
  }
}

export function formatUnixDate(
  unixDate: number,
  locale: LocaleString,
  options: FormatUnixDateOptions = {},
): string {
  const date = fromUnixTime(unixDate);
  if (!isValid(date)) return "";

  const { customFormat = "P", withoutYear, withTime } = options;

  let dateFormat = withoutYear
    ? locale === "de"
      ? "dd.MM."
      : "dd/MM"
    : customFormat;

  if (withTime) {
    dateFormat += " HH:mm";
  }

  return format(date, dateFormat, {
    locale: getLocale(locale),
  });
}

export const getWrittenDate = (
  date: number,
  locale: LocaleString,
  withYear?: boolean,
): string => {
  const parsedDate = fromUnixTime(date);
  if (!isValid(parsedDate)) return "";

  return format(parsedDate, `d. MMMM ${withYear ? "yyyy" : ""}`, {
    locale: getLocale(locale),
  });
};

export const FORMAT_DATE_TYPE = {
  DATE: 2,
  DATE_WITHOUT_YEAR: 3,
  DIFFERENCE: 1,
};

type FormatDateOptions = {
  withToday: boolean;
  withTomorrow: boolean;
  withYesterday: boolean;
};

const normalizeFormatDateOptions = (options: FormatDateOptions) => ({
  withToday:
    typeof options.withToday !== "undefined" ? options.withToday : true,
  withTomorrow:
    typeof options.withTomorrow !== "undefined" ? options.withTomorrow : true,
  withYesterday:
    typeof options.withYesterday !== "undefined" ? options.withYesterday : true,
});

export const isSameOrBeforeToday = (timestamp: number | null) => {
  if (!timestamp) return false;
  const date = fromUnixTime(timestamp);
  return isPast(date) || isToday(date);
};

export function isFutureDay(unixTimestamp: number) {
  const date = fromUnixTime(unixTimestamp);

  const startOfInputDay = startOfDay(date);
  const startOfToday = startOfDay(new Date());

  const result = isFuture(startOfInputDay) && startOfInputDay > startOfToday;

  return result;
}

export const formatDate = (
  unix: number | null | undefined,
  translations: Translations,
  localeCode?: LocaleString,
  format: number = FORMAT_DATE_TYPE.DIFFERENCE,
  options: FormatDateOptions = {
    withToday: true,
    withTomorrow: true,
    withYesterday: true,
  },
) => {
  if (!unix) return translations.general.missing;

  if (!Number.isFinite(unix) || unix < 0) {
    console.error("Invalid unix timestamp in formatDate:", {
      isInfinity: unix === Infinity || unix === -Infinity,
      isNaN: Number.isNaN(unix),
      stringValue: String(unix),
      type: typeof unix,
      value: unix,
    });
    return translations.general.missing;
  }

  try {
    const { withToday, withTomorrow, withYesterday } =
      normalizeFormatDateOptions(options);

    const dateFromUnix = fromUnixTime(unix);

    if (!isValid(dateFromUnix)) {
      console.error("fromUnixTime created invalid date:", {
        getTime: dateFromUnix.getTime(),
        input: unix,
        output: dateFromUnix,
      });
      return translations.general.missing;
    }

    const date = zonedTimeToUtc(dateFromUnix, "UTC");

    if (!isValid(date)) {
      console.error("zonedTimeToUtc created invalid date:", {
        getTime: date.getTime(),
        input: dateFromUnix,
        output: date,
      });
      return translations.general.missing;
    }

    const today = zonedTimeToUtc(new Date(), "UTC");

    if (withToday && isSameDay(date, today)) {
      return translations.general.today;
    }
    const yesterday = zonedTimeToUtc(subDays(new Date(), 1), "UTC");

    if (withYesterday && isSameDay(date, yesterday)) {
      return translations.general.yesterday;
    }

    const tomorrow = zonedTimeToUtc(addDays(new Date(), 1), "UTC");

    if (withTomorrow && isSameDay(date, tomorrow)) {
      return translations.general.tomorrow;
    }

    if (format === FORMAT_DATE_TYPE.DATE && localeCode) {
      return formatUnixDate(getUnixTime(date), localeCode);
    }

    if (format === FORMAT_DATE_TYPE.DATE_WITHOUT_YEAR && localeCode) {
      return formatUnixDate(getUnixTime(date), localeCode, {
        withoutYear: true,
      });
    }

    return formatDistance(startOfDay(date), startOfDay(today), {
      locale: getLocale(localeCode ?? "de"),
    });
  } catch (error) {
    console.error("RangeError in formatDate:", {
      error,
      stringValue: String(unix),
      type: typeof unix,
      unix,
    });
    return translations.general.missing;
  }
};

export const getDateDifferenceFromToday = (
  date: number,
  options?: { fromStartOfDay: boolean } | undefined,
) => {
  let today = zonedTimeToUtc(new Date(), "UTC");
  let utcDate = fromUnixTime(date);
  if (options?.fromStartOfDay) {
    today = startOfDay(today);
    utcDate = startOfDay(utcDate);
  }
  const duration = differenceInDays(utcDate, today);
  return duration;
};

export const getCareStartDate = (
  date: number | null | undefined,
  status: number | null | undefined,
  withPastDates = false,
) => {
  if (!date) return null;

  if (status != AUCTION_REQUEST_SENT && status != AUCTION_REQUEST_ACCEPTED) {
    return date;
  }

  if (!withPastDates) {
    const today = zonedTimeToUtc(startOfDay(new Date()), "UTC");
    return date < getUnixTime(today) ? getUnixTime(today) : date;
  }

  return date;
};

export const getDefaultReportStartDate = () =>
  getUnixTime(startOfDay(subDays(new Date(), 30)));

export const getDefaultReportEndDate = () =>
  getUnixTime(startOfDay(new Date()));

export const isPastDate = (
  v: number | null | undefined,
  days?: number,
): boolean => {
  // - by default checks if current day is equal today or is before it
  // - when provide days argument checks if date is equal day difference
  // from today or is before it
  if (v == null) return false;
  const date = days
    ? addDays(zonedTimeToUtc(new Date(), "UTC"), days)
    : zonedTimeToUtc(new Date(), "UTC");
  const unixValue = fromUnixTime(v);

  return isSameDay(unixValue, date) || isBefore(unixValue, date);
};

export const getLocale = (locale?: LocaleString): Locale => {
  switch (locale) {
    case "de":
      return de;
    case "en":
      return enGB;
    default:
      return de;
  }
};

export const convertBirthDateIn = (
  encryptedDate: EncryptedContent | { decrypted: string } | null | undefined,
  props: AnyObject & { locale: LocaleString },
) => {
  if (!encryptedDate) return null;
  const decryptedDate: string = convertEncryptedFieldIn(encryptedDate);
  const birthDate: Date = convertStringToDate(decryptedDate, "de");
  if (isValid(birthDate))
    return format(birthDate, "P", { locale: getLocale(props?.locale) });
  return null;
};

export const removeNumbers = (s: string) => {
  return s.replace(/\d/g, "x");
};

export const convertBirthDateOut = (
  formattedDate: string,
  props: AnyObject & { locale: LocaleString },
) => {
  if (!formattedDate) return null;
  try {
    const date = convertStringToDate(formattedDate, props?.locale);
    if (!isValid(date)) {
      let sanitizedDate = formattedDate;
      if (typeof sanitizedDate !== "string") {
        sanitizedDate = JSON.stringify(sanitizedDate);
      }
      console.error(
        `could not parse birth date "${removeNumbers(
          formattedDate,
        )}", numbers in string redacted`,
      );
      return null;
    }
    const sanitizedDate = format(date, "dd.MM.yyyy");
    return convertEncryptedFieldOut(sanitizedDate);
  } catch (err) {
    console.error(`error converting birth date out: ${getErrorMessage(err)}`);
    return null;
  }
};

export const convertBirthDateToFhir = (formattedDate: string) => {
  const dateValue = convertStringToDate(formattedDate, "de");
  if (isValid(dateValue)) return format(dateValue, "yyyy-MM-dd");
  return null;
};

export const convertStringToDate = (
  formattedDate: string,
  locale: LocaleString,
): Date => {
  let format = "dd.MM.yyyy";
  if (isMatch(formattedDate, "dd/MM/yyyy")) {
    format = "dd/MM/yyyy";
  }
  const date = parse(formattedDate, format, new Date(0), {
    locale: getLocale(locale),
  });

  return date;
};

export const validateBirthDate = (
  formattedDate: string,
  props: { locale?: LocaleString; translations?: Translations },
) => {
  const { locale = "de", translations } = props;
  if (!formattedDate) return false;

  const birthDate = convertStringToDate(formattedDate, locale);
  const minBirthDate = subYears(new Date(), MAX_AGE);

  const isValidDate =
    isValid(birthDate) &&
    isPast(birthDate) &&
    !isBefore(birthDate, minBirthDate) &&
    formattedDate.length >= "MM.DD.YYYY".length;

  if (isValidDate) {
    return true;
  }

  return (
    translations?.patient.medicalDiagnosis.operationDateValidation ?? false
  );
};

export const formatFromNow = (
  timestamp: Date | number,
  locale: LocaleString,
) => {
  if (!timestamp) return "";
  const date =
    typeof timestamp === "number" ? fromUnixTime(timestamp) : timestamp;
  return formatDistance(date, new Date(), {
    addSuffix: true,
    locale: getLocale(locale),
  });
};

export const formatDateString = (
  dateString: string,
  locale: Locale,
): string => {
  try {
    return format(parse(dateString, "dd/MM/yyyy", new Date()), "P", {
      locale,
    });
  } catch {
    return dateString;
  }
};

export function getDateValuesForFilters(
  locale: LocaleString,
  values: FilterValueStartDateRange | undefined,
  translations: Translations,
): string {
  if (values) {
    if (values.from_date && values.to_date) {
      return `${formatDate(
        values.from_date,
        translations,
        locale,
        FORMAT_DATE_TYPE.DATE,
        { withToday: false, withTomorrow: false, withYesterday: false },
      )} - ${formatDate(
        values.to_date,
        translations,
        locale,
        FORMAT_DATE_TYPE.DATE,
        { withToday: false, withTomorrow: false, withYesterday: false },
      )}`;
    }
    if (values.from_date && !values.to_date) {
      return `${translations.general.fromDate} ${formatDate(
        values.from_date,
        translations,
        locale,
        FORMAT_DATE_TYPE.DATE,
        { withToday: false, withTomorrow: false, withYesterday: false },
      )}`;
    }

    if (!values.from_date && values.to_date) {
      return `${translations.general.untilDate} ${formatDate(
        values.to_date,
        translations,
        locale,
        FORMAT_DATE_TYPE.DATE,
        { withToday: false, withTomorrow: false, withYesterday: false },
      )}`;
    }
  }
  return "";
}

export function getDateValue(
  value: number,
  locale: Locale,
  withTime?: boolean,
): string {
  if (isValid(value)) {
    if (withTime) {
      return format(fromUnixTime(value), "PPp", { locale });
    }
    return format(fromUnixTime(value), "PP", { locale });
  }
  return String(value);
}

export const getDateFromUnixString = (
  date: string | null | undefined,
): Date | null => {
  if (!date) return null;

  const unix = parseInt(date);
  if (isNaN(unix)) {
    return null;
  }

  const parsed = fromUnixTime(unix);
  if (!isValid(parsed)) {
    return null;
  }

  return parsed;
};

export const addDaysUnixUtc = (
  days: number,
  options?: { endOfDay?: boolean; startOfDay?: boolean },
) => {
  const date = options?.endOfDay
    ? endOfDay(new Date())
    : options?.startOfDay
    ? startOfDay(new Date())
    : new Date();

  return getUnixTime(zonedTimeToUtc(addDays(date, days), "UTC"));
};

export function formatIndependenceScaleDateAndScore(
  value: SingerAssessment | BarthelAssessment | EarlyRehabAssessment,
  locale: Locale,
) {
  return `${value.score} (${format(fromUnixTime(value.date as number), "P", {
    locale,
  })})`;
}

export const parseDate = (
  dateString: string | undefined | null,
  extraFormat = "",
): Date | undefined => {
  if (!dateString) {
    return undefined;
  }
  const trimmed = dateString.trim();
  if (!trimmed) {
    return undefined;
  }
  const formats: string[] = ["dd.MM.yyyy", "dd/MM/yyyy", "yyyy-MM-dd"];
  if (extraFormat) {
    formats.push(extraFormat);
  }
  const reference = new Date(0);
  for (const fmt of formats) {
    const parsedDate = parse(trimmed, fmt, reference);
    if (isValid(parsedDate)) {
      return parsedDate;
    }
  }
  return undefined;
};

export const parseDateStringToUnix = (
  dateString: string | undefined | null,
): number | undefined => {
  if (!dateString) return undefined;

  const parsedDate = parseDate(dateString);

  if (parsedDate && isValid(parsedDate)) {
    return getUnixTime(parsedDate);
  }

  return undefined;
};

/**
 * Normalizes a date value (ISO string, unix seconds, or null/undefined) to unix seconds.
 * Returns null for invalid or missing values.
 */
export function toUnixTimestamp(
  value: string | number | null | undefined,
): number | null {
  if (value == null) return null;
  if (typeof value === "number") return value;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : Math.floor(parsed / 1000);
}

export type TimePeriod =
  | "today"
  | "yesterday"
  | "lastWeek"
  | "lastMonth"
  | "lastYear"
  | "older";

export function getTimePeriodFromUnix(timestamp: number): TimePeriod {
  const messageDate = fromUnixTime(timestamp);
  const now = new Date();
  const yesterday = startOfDay(subDays(now, 1));
  const sevenDaysAgo = startOfDay(subDays(now, 7));
  const lastMonthStart = startOfDay(subMonths(now, 1));
  const lastYearStart = startOfYear(subYears(now, 1));
  const messageDay = startOfDay(messageDate);

  if (isToday(messageDate)) {
    return "today";
  }

  if (isYesterday(messageDate)) {
    return "yesterday";
  }

  if (
    isWithinInterval(messageDay, {
      end: subDays(yesterday, 1),
      start: sevenDaysAgo,
    })
  ) {
    return "lastWeek";
  }

  if (
    isWithinInterval(messageDay, {
      end: subDays(sevenDaysAgo, 1),
      start: lastMonthStart,
    })
  ) {
    return "lastMonth";
  }

  if (
    isWithinInterval(messageDay, {
      end: subDays(lastMonthStart, 1),
      start: lastYearStart,
    })
  ) {
    return "lastYear";
  }

  return "older";
}

export function isOlderThanOneHour(timestamp: number): boolean {
  const messageDate = fromUnixTime(timestamp);
  const now = new Date();
  const hoursDiff = differenceInHours(now, messageDate);
  return hoursDiff >= 1;
}

function getPeriodLabelFromPeriod(
  period: TimePeriod,
  translations: Translations,
): string {
  switch (period) {
    case "today":
      return translations.general.today;
    case "yesterday":
      return translations.general.yesterday;
    case "lastWeek":
      return translations.general.lastWeek;
    case "lastMonth":
      return translations.general.lastMonth;
    case "older":
      return translations.general.moreThanAYearAgo;
    default:
      return "";
  }
}

export function getPeriodLabel(
  unixTimestamp: number,
  translations: Translations,
  locale?: LocaleString,
): string {
  const period = getTimePeriodFromUnix(unixTimestamp);

  if (period === "lastYear") {
    const date = fromUnixTime(unixTimestamp);
    return format(date, "MMMM yyyy", { locale: getLocale(locale) });
  }

  return getPeriodLabelFromPeriod(period, translations);
}

export const AHB_SUBMISSION_TIMEZONE = "Europe/Berlin";

export const AHB_DATE_FORMAT: Record<SchemaVersion, string> = {
  [SCHEMA_VERSION.V1_1_0]: "yyyyMMdd",
  [SCHEMA_VERSION.V2_0_0]: "yyyy-MM-dd",
};

export function formatAhbXmlDate(
  value: Date | number | null | undefined,
  schemaVersion: SchemaVersion,
): string | undefined {
  if (value === null || value === undefined) {
    return undefined;
  }

  if (typeof value === "number") {
    if (!Number.isFinite(value) || value === 0) {
      return undefined;
    }
    return format(fromUnixTime(value), AHB_DATE_FORMAT[schemaVersion]);
  }

  const zonedDate = utcToZonedTime(value, AHB_SUBMISSION_TIMEZONE);
  return formatInBerlin(zonedDate, AHB_DATE_FORMAT[schemaVersion]);
}
